import React, { useState, useCallback, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { useDebounce } from 'use-debounce';
import { editorAPI } from '@/lib/editor/api';
import { usePermissions } from '@/hooks/usePermissions';
import { useOnboardingMark } from '@/contexts/onboarding-context';

interface CodeEditorProps {
  projectId: string;
  file: { path: string; content: string } | null;
  onFileSave?: (content: string) => void;
}

export const CodeEditor: React.FC<CodeEditorProps> = ({ projectId, file, onFileSave }) => {
  const { canEditFiles, isViewer } = usePermissions();
  const markFirstEdit = useOnboardingMark();
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [debouncedContent] = useDebounce(content, 1000);

  useEffect(() => {
    if (file) {
      setContent(file.content);
    }
  }, [file]);

  // Auto-save on debounced content change (skip for viewers)
  useEffect(() => {
    if (canEditFiles && file && debouncedContent !== file.content && debouncedContent !== '') {
      handleSave();
    }
  }, [debouncedContent, canEditFiles]);

  const handleSave = async () => {
    if (!file || !canEditFiles) return;
    
    setSaving(true);
    try {
      await editorAPI.saveFile(projectId, file.path, content);
      markFirstEdit();
      onFileSave?.(content);
    } catch (error) {
      console.error('Failed to save file:', error);
      // Call onFileSave even if backend fails, to save locally
      onFileSave?.(content);
    } finally {
      setSaving(false);
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    setContent(value || '');
  };

  // Manual save with Ctrl+S
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  }, [content, file]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!file) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        Select a file to edit
      </div>
    );
  }

  const getLanguage = (path: string) => {
    const ext = path.split('.').pop();
    const languageMap: Record<string, string> = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript', 
      'tsx': 'typescript',
      'css': 'css',
      'scss': 'scss',
      'html': 'html',
      'json': 'json',
      'md': 'markdown',
      'py': 'python',
      'sql': 'sql',
      'yaml': 'yaml',
      'yml': 'yaml',
      'xml': 'xml',
      'sh': 'shell',
      'bash': 'shell',
    };
    return languageMap[ext || ''] || 'plaintext';
  };

  return (
    <div className="relative h-full">
      {saving && (
        <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-purple-600 text-white text-sm rounded">
          Saving...
        </div>
      )}
      {isViewer && (
        <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-gray-600 text-white text-sm rounded">
          View only
        </div>
      )}
      <Editor
        height="100%"
        language={getLanguage(file.path)}
        value={content}
        onChange={handleEditorChange}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          wordWrap: 'on',
          formatOnPaste: canEditFiles,
          formatOnType: canEditFiles,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          padding: { top: 10 },
          readOnly: !canEditFiles,
        }}
      />
    </div>
  );
};